import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, ResponsiveContainer, Cell, Legend, Tooltip } from 'recharts';
import apiService from '../../services/api/api.service';

const ShareholdingDashboard = ({ uniqueTitle }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareholdingData, setShareholdingData] = useState(null);
  const [chartRadius, setChartRadius] = useState(100);

  const chartRef = useRef(null);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await apiService.getProductByTitle(uniqueTitle);

        if (result?.success && result?.data?.shareholding) {
          setShareholdingData(result.data.shareholding);
        } else {
          throw new Error('Shareholding data not found.');
        }
      } catch (err) {
        console.error('Error fetching shareholding data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (uniqueTitle) {
      fetchData();
    }
  }, [uniqueTitle]);

  useEffect(() => {
    const resizeHandler = () => {
      if (chartRef.current) {
        const width = chartRef.current.offsetWidth;
        const newRadius = Math.max(60, Math.min(width / 3, 120)); // clamp radius
        setChartRadius(newRadius);
      }
    };

    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  if (loading) return <div className="p-4 text-center text-gray-700">Loading shareholding data...</div>;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;
  if (!shareholdingData) return <div className="p-4 text-center text-gray-500">No shareholding data available.</div>;

  const summaryData = shareholdingData.summary || {};
  const tableData = shareholdingData.table || [];
  const majorShareholdersData = tableData
    .filter(item => item.shareholdingAbove5 && item.holdingPercentage)
    .map(item => ({
      name: item.shareholdingAbove5,
      value: item.holdingPercentage
    }));

  const totalMajorShareholdersPercentage = majorShareholdersData.reduce((total, item) => total + item.value, 0);
  if (totalMajorShareholdersPercentage < 100) {
    majorShareholdersData.push({
      name: 'Others',
      value: +(100 - totalMajorShareholdersPercentage).toFixed(2)
    });
  }

  const ownershipData = [
    { name: 'Promoter', value: summaryData.promoterPercentage || 0 },
    { name: 'Public', value: summaryData.publicPercentage || 0 }
  ];

  return (
    <div className="max-w-screen-lg mx-auto p-4 sm:p-6 bg-gray-50">
      <h1 className="text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6">
        Company Shareholding Information ({summaryData.year || 'N/A'})
      </h1>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 sm:mb-8">
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-2">Ownership Summary</h2>
          <p>Promoter: {summaryData.promoterPercentage || 0}%</p>
          <p>Public: {summaryData.publicPercentage || 0}%</p>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-2">Shareholder Details</h2>
          <p>Total Shareholders: {summaryData.numberOfShareholders?.toLocaleString() || 0}</p>
          <p>Promoter Shareholders: {summaryData.numberOfPromoterShareholders || 0}</p>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-2">Share Volume</h2>
          <p>Total Equity Shares: {summaryData.totalEquityShares?.toLocaleString() || 0}</p>
          <p>Total Preference Shares: {summaryData.totalPreferenceShares?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 sm:mb-8" ref={chartRef}>
        {/* Major Shareholders */}
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-base sm:text-lg font-semibold text-center mb-2">Major Shareholders</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={majorShareholdersData}
                cx="50%"
                cy="50%"
                outerRadius={chartRadius}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {majorShareholdersData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Promoter vs Public Shareholding */}
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-base sm:text-lg font-semibold text-center mb-2">Promoter vs Public Shareholding</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={ownershipData}
                cx="50%"
                cy="50%"
                outerRadius={chartRadius}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {ownershipData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Shareholders Holding > 5% */}
      {majorShareholdersData.length > 0 && (
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-4">Shareholders Holding &gt; 5%</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm sm:text-base">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase">
                    Shareholder Name
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase">
                    Holding Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {majorShareholdersData.map((shareholder, index) => (
                  <tr key={index}>
                    <td className="px-4 sm:px-6 py-4">{shareholder.name}</td>
                    <td className="px-4 sm:px-6 py-4">{shareholder.value}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareholdingDashboard;
